// Notifications : la cloche (lues / non lues) et le push sur l'appareil (D-017).
import { supabase } from './supabase';

export interface Notif { id: string; kind: string; title: string; body: string | null; url: string | null; created_at: string; read_at: string | null;
  /** Sa clé (la séance, la date, le binôme…) et la personne dont elle parle : où elle mène (src/domain/notif-links.ts). */
  dedup_key?: string | null; about_person?: string | null }

const db = () => {
  if (!supabase) throw new Error('Base non configurée');
  return supabase;
};

export async function listNotifications(): Promise<Notif[]> {
  const { data, error } = await db().from('notifications')
    .select('id, kind, title, body, url, created_at, read_at, dedup_key, about_person').order('created_at', { ascending: false }).limit(60);
  if (error) throw new Error(error.message);
  return data ?? [];
}

export async function unreadCount(): Promise<number> {
  const { count } = await db().from('notifications').select('id', { count: 'exact', head: true }).is('read_at', null);
  return count ?? 0;
}

export async function markRead(ids: string[]): Promise<void> {
  if (!ids.length) return;
  await db().from('notifications').update({ read_at: new Date().toISOString() }).in('id', ids).is('read_at', null);
}

/** Notifications d'annonces lues sur l'écran Annonces : plus de pastille pour elles. */
export async function markAnnouncementNotifsRead(announcementIds: string[]): Promise<void> {
  if (!announcementIds.length) return;
  await db().from('notifications').update({ read_at: new Date().toISOString() })
    .eq('kind', 'announcement').in('url', announcementIds.map(id => `#/annonces/${id}`)).is('read_at', null);
}

// --- Push sur cet appareil -------------------------------------------------------

const VAPID = import.meta.env.VITE_VAPID_PUBLIC_KEY as string | undefined;

/** 'ok' ; 'ios-install' = iPhone : il faut d'abord ajouter l'app à l'écran d'accueil. */
export function pushSupport(): 'ok' | 'ios-install' | 'unsupported' {
  const ios = /iP(hone|ad|od)/.test(navigator.userAgent);
  if (!VAPID) return 'unsupported';
  if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) return ios ? 'ios-install' : 'unsupported';
  return 'ok';
}

const registration = () => navigator.serviceWorker.getRegistration('./');

export async function pushState(): Promise<'on' | 'off' | 'denied'> {
  if (pushSupport() !== 'ok') return 'off';
  if (Notification.permission === 'denied') return 'denied';
  const sub = await (await registration())?.pushManager.getSubscription();
  return sub && Notification.permission === 'granted' ? 'on' : 'off';
}

function keyBytes(b64url: string): Uint8Array<ArrayBuffer> {
  const s = atob(b64url.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat((4 - (b64url.length % 4)) % 4));
  return Uint8Array.from(s, c => c.charCodeAt(0));
}

async function save(sub: PushSubscription): Promise<void> {
  const j = sub.toJSON();
  const { error } = await db().rpc('register_push', {
    p_endpoint: j.endpoint, p_p256dh: j.keys?.p256dh, p_auth: j.keys?.auth, p_user_agent: navigator.userAgent,
  });
  if (error) throw new Error(error.message);
}

export async function enablePush(): Promise<'on' | 'denied' | 'error'> {
  try {
    const perm = await Notification.requestPermission();
    if (perm !== 'granted') return 'denied';
    const reg = await navigator.serviceWorker.register('./sw.js', { scope: './' });
    await navigator.serviceWorker.ready;
    const sub = (await reg.pushManager.getSubscription())
      ?? await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes(VAPID!) });
    await save(sub);
    return 'on';
  } catch {
    return 'error';
  }
}

/** Coupe le push de cet appareil pour le compte connecté. */
export async function disablePush(): Promise<void> {
  const sub = await (await registration())?.pushManager.getSubscription();
  if (!sub) return;
  await db().from('push_subscriptions').delete().eq('endpoint', sub.endpoint);
  await sub.unsubscribe().catch(() => undefined);
}

/** Après connexion : l'appareil déjà abonné passe au compte connecté. */
export async function syncPush(): Promise<void> {
  if (pushSupport() !== 'ok' || Notification.permission !== 'granted') return;
  const sub = await (await registration())?.pushManager.getSubscription();
  if (sub) await save(sub).catch(() => undefined);
}
